import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Dialog } from "component/shared/dialog";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { Agent } from "model/agent";
import { createAgent, resetError} from "redux/slice/agents";
import { getBoardsForUser } from "redux/selector";
import { getOffices } from "redux/selector";
import { RootState, getPayload } from "redux/store";
import { styles } from "./style";
import { LabeledField } from "./labeled-field";
import { updateRoundRobinRoutes } from "redux/slice/round-robin-routes";
import { NewRoundRobinRoute } from "model/round-robin-route";
import MuiAlert, { AlertColor as Color } from "@mui/material/Alert";
import {getLanguages, getRoundRobinRoutes} from "redux/selector";
import * as env from "shared/env";
import * as yup from "yup";
import { getUser, getIsAtLeadOwnershipLimit, getMaxLeadOwners } from "redux/selector";
import Checkbox from '@mui/material/Checkbox';
import { validate } from "shared/yup";
import * as Router from "react-router-dom";
import { urls } from "routes/urls";
import * as paths from "routes/paths";
import { AgentStaffType } from "type/agent-staff";

interface Props extends
	OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	Router.RouteComponentProps,
	WithStyles<typeof styles> {}

interface OwnProps {
	agents: Agent[];
	onClose: () => void;
	open: boolean;
}

interface State {
	lastName: string;
	firstName: string;
	email: string;
	username: string;
	password: string;
	admin: boolean;
	ownsLeads: boolean,
	openSnackbar: boolean;
	snackbarMessage: string;
	snackbarSeverity?: Color;
	roundRobin: boolean
	submitted: boolean;
}

const mapStateToProps = (state: RootState) => {
	return {
		loading: state.agentBoards.loading || state.boards.loading,
		boards: getBoardsForUser(state),
		offices: getOffices(state),
		languages: getLanguages(state),
		currentroundRobinRoutes: getRoundRobinRoutes(state),
		agentError: state.agents.error,
		user : getUser(state),
		usernameConflict: state.agents.error === "conflict",
		isAtLeadOwnershipLimit: getIsAtLeadOwnershipLimit(state),
		maxLeadOwners: getMaxLeadOwners(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateRoundRobinRoutes,
			createAgent,
			resetError,
		},
		dispatch
	);

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			lastName: "",
			firstName: "",
			username: "",
			password: "",
			email: "",	
			submitted: false,
			openSnackbar: false,
			snackbarMessage: "",
			roundRobin: false,
			admin: false,
			ownsLeads: true,
		};
	}

	public componentDidMount() {
		this.props.resetError();
	}

	public componentDidUpdate(prevProps: Props) {
		const { currentroundRobinRoutes, agents, user, history } = this.props;
		const { username, roundRobin } = this.state;
		if (prevProps.usernameConflict !== this.props.usernameConflict) {
			if (this.props.usernameConflict) {
				this.setState({
					openSnackbar: true,
					snackbarMessage: "Please fix the username errors and try again",
					snackbarSeverity: "error",
				})
			}
		}
		if (
			prevProps.agentError !== this.props.agentError &&
			this.props.agentError &&
			!this.props.usernameConflict
		) {
			this.setState({
				openSnackbar: true,
				snackbarMessage: "We couldn't add this team member. Please check the form and try again.",
				snackbarSeverity: "error",
			});
			this.props.resetError();
		}
		if (prevProps.agents.length !== agents.length) {
			const roundRobinPermission = user.roundRobinRouting && user.permissions.roundRobinRouting;
			const newAgent = agents.find((agent) => agent.username === username);
			if (newAgent) {
				if (roundRobinPermission && roundRobin) {
					const roundRobinRoutes = currentroundRobinRoutes.map(roundRobinRoute => ({
						agent: roundRobinRoute.agent,
					}));
					roundRobinRoutes.push({
						agent: newAgent,
					});
					this.props.updateRoundRobinRoutes({ roundRobinRoutes });
				}
				history.push(`${paths.settingsAgents}?success=true`);
				this.props.onClose();
			}
		}
	}

	private submit = async () => {
		const { user, agents, isAtLeadOwnershipLimit } = this.props;
		const {
			lastName,
			firstName,
			email,
			username,
			password,
			admin,
			ownsLeads,
		} = this.state;
		const errors = validate(this.validationSchema, {...this.state});
		this.setState({ submitted: true });
		if (!errors) {
			this.props.createAgent( { agent:
				{
					firstName: firstName,
					lastName: lastName,
					email: email,
					username: username,
					password: password,
					hasOwnSite: false,
					languageIds: [],
					admin: admin,
					staffType: AgentStaffType.default,
					ownsLeads: isAtLeadOwnershipLimit ? false : ownsLeads,
					showOnSignupDropDown: true,
					allowModifyBio: true,
					allowModifyListings: true,
					allowModifySubscribers: true,
					enableAgentLink: true
				}
			});
		}
	};

	private validationSchema = {
		firstName: yup.string().trim().required("First name is required."),
		lastName: yup.string().trim().required("Last name is required."),
		email: yup.string().email("Please enter a valid email address.").required("Email is required."),
		username: yup
			.string()
			.trim()
			.required("Username is required.")
			.min(4, "Username must be 4-75 characters")
			.max(75, "Username must be 4-75 characters"),
		password: yup
			.string()
			.required("Password is required.")
			.min(4, "Password must be 4-75 characters")
			.max(75, "Password must be 4-75 characters"),
	};
	
	render() {
		const { classes = {}, loading, onClose, open, usernameConflict, user, agents, isAtLeadOwnershipLimit, maxLeadOwners } = this.props;
		const {
			lastName,
			firstName,
			email,
			username,
			password,
			admin,
			ownsLeads,
			submitted,
			openSnackbar,
			snackbarMessage,
			snackbarSeverity,
			roundRobin,
		} = this.state;
		const errors = validate(this.validationSchema, {...this.state});
		const roundRobinPermission = user.roundRobinRouting && user.permissions.roundRobinRouting;
		
		return (
			<Dialog
				disableBackdropClick
				classes={{ paper: classes.dialogPaper }}
				onClose={onClose}
				open={open}
				scroll="paper"
			>
				<Mui.DialogTitle>
					<Mui.Grid container direction="column">
						<Mui.Grid item>
							<Mui.Typography component="h2" variant="h4">
								Add Member
							</Mui.Typography>
						</Mui.Grid>
					</Mui.Grid>
					<Mui.IconButton
						className={classes.closeButton}
						onClick={() => this.props.onClose()}
					>
						<Icons.X />
					</Mui.IconButton>
					<Mui.Divider className={classes.titleDivider} />
				</Mui.DialogTitle>
				<Mui.DialogContent>
				<div className={classes.body}>
						{loading ? (
								<LoadingIndicator enable />
						) : (
							<>
								{!user.permissions.showTeamFeatures && (
									<div>
										<MuiAlert severity="warning" className={classes.warningBanner}>
											<Mui.Typography>
												Adding an agent may increase your cost.{" "}
												<Mui.Link
													href="https://www.ihomefinder.com/platform-pricing/#additional-agents"
													target="_blank"
													rel="noopener noreferrer"
													className={classes.pricingLink}
												>
													Learn more about pricing
													<Icons.ExternalLink size={16} />
												</Mui.Link>
											</Mui.Typography>
										</MuiAlert>
									</div>
								)}

								{/* General section — all fields required */}
								<div>
									<Mui.Typography variant="subtitle1" className={classes.sectionTitle}>
										General
									</Mui.Typography>
									<Mui.Grid container spacing={2}>
										<Mui.Grid item xs={12} sm={6}>
											<LabeledField
												label="First Name"
												required
												error={submitted && !!errors && !!errors.firstName}
												helperText={errors && errors.firstName}
												value={firstName}
												onChange={(event) => this.setState({ firstName: event.target.value })}
											/>
										</Mui.Grid>
										<Mui.Grid item xs={12} sm={6}>
											<LabeledField
												label="Last Name"
												required
												error={submitted && !!errors && !!errors.lastName}
												helperText={errors && errors.lastName}
												value={lastName}
												onChange={(event) => this.setState({ lastName: event.target.value })}
											/>
										</Mui.Grid>
										<Mui.Grid item xs={12}>
											<LabeledField
												label="Email"
												required
												error={submitted && !!errors && !!errors.email}
												helperText={errors && errors.email}
												value={email}
												onChange={(event) => this.setState({ email: event.target.value })}
											/>
										</Mui.Grid>
										<Mui.Grid item xs={12}>
											<LabeledField
												label="Username"
												required
												error={(submitted && !!usernameConflict) || !!(submitted && errors && errors.username)}
												helperText={
													!!usernameConflict ? "Please choose a different username and try again" :
													errors && errors.username
												}
												inputProps={{ autoComplete: "off" }}
												value={username}
												onChange={(event) => {
													if (usernameConflict) {
														this.props.resetError();
													}
													this.setState({ username: event.target.value });
												}}
											/>
										</Mui.Grid>
										<Mui.Grid item xs={12}>
											<LabeledField
												label="Password"
												required
												type="password"
												autoComplete="new-password"
												error={submitted && !!errors && !!errors.password}
												helperText={errors && errors.password}
												value={password}
												onChange={(event) => this.setState({ password: event.target.value })}
											/>
										</Mui.Grid>
									</Mui.Grid>
								</div>

								{/* Roles section */}
								<div>
									<Mui.Divider className={classes.sectionDivider} />
									<Mui.Typography variant="subtitle1" className={classes.sectionTitle}>
										Roles
									</Mui.Typography>
									<Mui.Tooltip
										title={isAtLeadOwnershipLimit ? `Limited to ${maxLeadOwners} agent${maxLeadOwners > 1 ? 's' : ''}` : ""}
										classes={{ tooltip: classes.toolTip }}
									>
										<Mui.FormControlLabel
											className={classes.checkboxOption}
											control={
												<Checkbox
													className={classes.checkboxControl}
													disableRipple
													checked={ownsLeads && !isAtLeadOwnershipLimit}
													onChange={() => this.setState({ ownsLeads: !ownsLeads })}
													name="manages-leads"
													disabled={isAtLeadOwnershipLimit}
												/>
											}
											label={
												<div>
													<Mui.Typography className={classes.checkboxOptionTitle}>
														Agent (Billable)
													</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.checkboxOptionDescription}>
														Agents have access to their own leads, featured listings, and performance dashboard.
													</Mui.Typography>
												</div>
											}
										/>
									</Mui.Tooltip>
									{!user.permissions.oneCrm && (
										<Mui.FormControlLabel
											className={classes.checkboxOption}
											control={
												<Checkbox
													className={classes.checkboxControl}
													disableRipple
													checked={admin}
													onChange={() => this.setState({ admin: !admin })}
													name="admin"
												/>
											}
											label={
												<div>
													<Mui.Typography className={classes.checkboxOptionTitle}>
														Admin
													</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.checkboxOptionDescription}>
														Admins have full access to all parts of the Control Panel, including team settings, all leads and listings, and account configuration.
													</Mui.Typography>
												</div>
											}
										/>
									)}
								</div>

								{/* Lead Routing section */}
								{roundRobinPermission && (
									<div>
										<Mui.Divider className={classes.sectionDivider} />
										<Mui.Typography variant="subtitle1" className={classes.sectionTitle}>
											Lead Routing
										</Mui.Typography>
										<Mui.FormControlLabel
											className={classes.checkboxOption}
											control={
												<Checkbox
													className={classes.checkboxControl}
													disableRipple
													checked={roundRobin}
													name="roundRobin"
													onChange={(event) => this.setState({ roundRobin: event.target.checked })}
												/>
											}
											label={
												<div>
													<Mui.Typography className={classes.checkboxOptionTitle}>
														Add to Round Robin rotation
													</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.checkboxOptionDescription}>
														Leads will be assigned to this agent based on set rotation order. Rotation order can be configured under{" "}
														<Mui.Link
															component={Router.Link}
															to={paths.settingsleadRouting}
															className={classes.leadRoutingLink}
														>
															Lead Routing
															<Icons.ExternalLink size={14} />
														</Mui.Link>
														.
													</Mui.Typography>
												</div>
											}
										/>
									</div>
								)}

							</>
						)}
					</div>
					<Mui.Snackbar
						action={
							<Mui.IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() =>
									this.setState({
										openSnackbar: false,
									})
								}
							>
								<Icons.Link fontSize="small" />
							</Mui.IconButton>
						}
						autoHideDuration={6000}
						onClose={() =>
							this.setState({
								openSnackbar: false,
							})
						}
						open={openSnackbar}
					>
						<MuiAlert severity={snackbarSeverity}>
							<Mui.Typography>{snackbarMessage}</Mui.Typography>
						</MuiAlert>
					</Mui.Snackbar>
				</Mui.DialogContent>
				{!loading && (
					<Mui.DialogActions className={classes.dialogActions}>
						<Mui.Button
							className={classes.button}
							variant="outlined"
							color={"primary"}
							onClick={() => this.props.onClose()}
						>
							Cancel
						</Mui.Button>
						<Mui.Button
							className={classes.button}
							variant="contained"
							color={"primary"}
							onClick={() => this.submit()}
							disabled={submitted && !!errors}
						>
							Add User
						</Mui.Button>
					</Mui.DialogActions>
				)}
			</Dialog>
		);
	}
}

export const AddMemberDialog = Router.withRouter(withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles));
